// Lectura de les metadades que els SVG exportats des de l'Aleta
// (backend/utils/plantilla-svg) embeuen a cada <rect>: la clau de la caixa
// serialitzada a l'id i els atributs data-pilar/data-cordo. Best-effort:
// els SVG externs sense aquestes metadades es comporten com sempre.
import { v4 as uuidv4 } from 'uuid';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UUID_WITH_SUFFIX_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[^-]/;
const CONTROL_CHAR_REGEX = /[\u0000-\u001f\u007f]/;

// Decodificador de claus de caixa serialitzades a l'id ("aleta-" + cada
// caràcter no segur com _hex_). Les claus poden ser qualsevol string: uuid,
// numèriques (.aleta importats verbatim), etc.
// Manté aquest format sincronitzat amb el codificador del backend:
// backend/utils/plantilla-svg/caixaId.js
const ALETA_ID_PREFIX = 'aleta-';
const MAX_CODE_POINT = 0x10ffff;

const decodeCaixaId = id => {
    if (typeof id !== 'string' || !id.startsWith(ALETA_ID_PREFIX)) return null;

    const encoded = id.slice(ALETA_ID_PREFIX.length);
    if (encoded === '') return null;

    return encoded.replace(/_([0-9a-f]+)_/g, (match, hex) => {
        const codePoint = parseInt(hex, 16);
        return codePoint <= MAX_CODE_POINT ? String.fromCodePoint(codePoint) : match;
    });
};

// Illustrator reanomena els ids duplicats afegint-hi _1_, _2_..., que el
// decodificador llegeix com a hex (_1_ → \u0001, _21_ → "!", _30_ → "0"). Aquests rects són
// còpies d'una caixa: han de ser caixes noves, no claus amb caràcters de control.
// Les còpies d'Inkscape (uuid-1, uuid-1-3) ja són claus vàlides amb posicions
// guardades i es conserven.
// Manté aquesta regla sincronitzada amb isIllustratorCopyKey del backend:
// backend/utils/plantilla-svg/caixaId.js
const isCopiedKey = key =>
    CONTROL_CHAR_REGEX.test(key) || UUID_WITH_SUFFIX_REGEX.test(key);

const parseIntAttribute = value => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

export const readEmbeddedMetadata = rectElement => {
    const pilar = parseIntAttribute(rectElement.getAttribute('data-pilar'));
    const cordo = parseIntAttribute(rectElement.getAttribute('data-cordo'));

    return {
        ...(pilar !== undefined ? { pilar } : {}),
        ...(cordo !== undefined ? { cordo } : {}),
    };
};

// Reutilitza les claus embegudes als SVG exportats des de l'Aleta perquè
// baixar -> editar -> tornar a pujar no canviï els ids de les caixes
// (les posicions guardades de les proves hi fan referència). També accepta
// ids que són directament un uuid (fitxers editats a mà).
export const deriveCaixaIds = rectElements => {
    const usedIds = new Set();

    return rectElements.map(rectElement => {
        const rawId = rectElement.getAttribute('id') || '';
        const decodedKey = decodeCaixaId(rawId);
        const candidate =
            decodedKey !== null ? (isCopiedKey(decodedKey) ? null : decodedKey) :
            UUID_REGEX.test(rawId.toLowerCase()) ? rawId.toLowerCase() :
            null;

        if (candidate !== null && !usedIds.has(candidate)) {
            usedIds.add(candidate);
            return candidate;
        }

        return uuidv4();
    });
};
